import moment from 'moment';
import { getAxisName } from './getAxisName';
import { max, min, uniq } from 'lodash';
import { roundExp } from './util';

/**
 * Get data slot
 * This function returns the records into a datatime range
 * @param array data = [{ timestamp, val }]
 * @param timestamp start
 * @param timestamp end
 */
export const getDataSlot = (data, start, end) => {
  const dataSlot = data.filter(
    (d) => d.timestamp >= start && d.timestamp < end
  );
  return dataSlot;
};

/**
 * Get min max values data slot
 * This function retorns
 */
export const getMinMaxValuesDataSlot = (dataSlot) => {
  /* select the biggest and the lowest value */
  if (dataSlot.length < 3) {
    return dataSlot;
  }

  const indexMinValue = dataSlot
    .map((d) => d.val)
    .indexOf(min(dataSlot.map((d) => d.val)));
  const indexMaxValue = dataSlot
    .map((d) => d.val)
    .indexOf(max(dataSlot.map((d) => d.val)));
  if (indexMinValue === -1 || indexMaxValue === -1) {
    return dataSlot;
  }

  const newDataSlot = [];
  if (dataSlot[indexMinValue].timestamp === dataSlot[indexMaxValue].timestamp) {
    return [dataSlot[indexMinValue]];
  }

  if (dataSlot[indexMinValue].timestamp > dataSlot[indexMaxValue].timestamp) {
    newDataSlot.push(dataSlot[indexMaxValue]);
    newDataSlot.push(dataSlot[indexMinValue]);
  } else {
    newDataSlot.push(dataSlot[indexMinValue]);
    newDataSlot.push(dataSlot[indexMaxValue]);
  }
  return newDataSlot;
};

/**
 * Reduce to data slots min and max values
 * @param array data: [{ timestamp, val }]
 * @param int pixels: max width or pixels to be shown
 * @return array newData: reduce array
 */
export const reduceToDataSlotMinMaxValues = (data, pixels) => {
  const timeSlot = Math.ceil(
    (data[data.length - 1].timestamp - data[0].timestamp) / pixels
  );
  const totalSlots = Math.ceil(pixels);
  let index = 0;
  let newData = [];
  while (index < totalSlots) {
    /* get all the values inside the slot */
    const dataSlot = getMinMaxValuesDataSlot(
      getDataSlot(
        data,
        data[0].timestamp + index * timeSlot,
        index + 1 === totalSlots
          ? data[0].timestamp + index * timeSlot + timeSlot + 1
          : data[0].timestamp + index * timeSlot + timeSlot
      )
    );
    newData = newData.concat(dataSlot);
    index += 1;
  }
  return newData;
};

/**
 * Reduce data to amount pixels
 * This function reduces the data to be displayed into the charts to the number
 * of pixels (width)
 * @param array data: [{ timestamp, val }]
 * @param int pixels: max width or pixels to be shown
 * @return object dataReducedIndexedByTimestamp: {timestamp: [val]}
 */
const reduceDataToAmountPixels = (data, pixels) => {
  /* Reduce size grouping values into slot times and reduce the values in the slot
  to the max and min value */
  const dataReduced = reduceToDataSlotMinMaxValues(data, pixels);
  const dataReducedIndexedByTimestamp = {};
  dataReduced.forEach((d) => {
    dataReducedIndexedByTimestamp[d.timestamp] = d;
  });
  return dataReducedIndexedByTimestamp;
};

/**
 * Get time list gaps
 * generate a list of times when no data was received and therefore a gap should
 * be displayed until the next valid data is received.
 * @param array dataSet
 * @return array timeList (timestamp)
 */
export const getTimeListGaps = (dataSet) => {
  try {
    const timeList = [];
    if (dataSet.length === 0) {
      return [];
    }
    let timeDiff = [];
    /* select the dataSet with more data */
    const dataSetMaxPoints = dataSet.find(
      (set) =>
        set.data.length === Math.max(...dataSet.map((d) => d.data.length))
    );

    dataSetMaxPoints.data.forEach((d, index) => {
      if (index > 0 && index < dataSetMaxPoints.data.length - 1) {
        timeDiff.push(dataSetMaxPoints.data[index + 1].timestamp - d.timestamp);
      }
    });
    /* sort asc the time difference in seconds */
    timeDiff = timeDiff.sort((a, b) => a - b);

    /* get the arithmetic media of the average values */
    const lowMiddle = Math.floor((timeDiff.length - 1) / 2);
    const highMiddle = Math.ceil((timeDiff.length - 1) / 2);
    const media = (timeDiff[lowMiddle] + timeDiff[highMiddle]) / 2;

    /* Create array with the number of times the time differences are repeated */
    const timeDiffUnique = uniq(timeDiff);
    const timeDiffTotal = [];
    timeDiffUnique.forEach((t) => {
      timeDiffTotal.push({
        diff: t,
        total: timeDiff.filter((td) => td === t).length,
      });
    });

    /* Split in different array the time difference between the points that occurs
    only 1 time and several times */
    let timeDiffFilter1Time = [];
    const timeDiffFilterMoreTimes = [];
    timeDiffTotal.forEach((t) => {
      if (t.total === 1) {
        timeDiffFilter1Time.push(t);
      } else {
        timeDiffFilterMoreTimes.push(t);
      }
    });
    if (timeDiffFilter1Time.length === 0) {
      return [];
    }
    let threshold;

    /* filter timeDiffFilter1Time with values bigger as the media or the biggest value
    in the array timeDiffFilterMoreTimes */
    if (timeDiffFilterMoreTimes.length > 0) {
      timeDiffFilter1Time =
        media > timeDiffFilterMoreTimes[timeDiffFilterMoreTimes.length - 1].diff
          ? timeDiffFilter1Time.filter((t) => t.diff > media)
          : timeDiffFilter1Time.filter(
              (t) =>
                t.diff >
                timeDiffFilterMoreTimes[timeDiffFilterMoreTimes.length - 1].diff
            );
      threshold = timeDiffFilter1Time[0]?.diff;
    }

    if (!threshold) {
      return [];
    }

    /* Find all the timestamps with a time diference bigger as the threshold */
    dataSet[0].data.forEach((d, index) => {
      if (index > 0 && index < dataSet[0].data.length - 1) {
        if (dataSet[0].data[index + 1].timestamp - d.timestamp >= threshold) {
          timeList.push(d.timestamp);
        }
      }
    });
    return timeList;
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Convert data
 * @param array dataset
 * @param object axisStyle
 * @param number width
 * @param int timestampMin = timestamp
 * @param int timestampMax = timestamp
 * @return object = {sample, seriesConfig, axes}
 */
export const convertData = ({
  dataset,
  axisStyle,
  width,
  height,
  timestampMin = null,
  timestampMax = null,
  withLabels = true,
  maxVAxis,
}) => {
  if (dataset.length < 1) {
    return [];
  }
  /* clone shallow dataset */
  let datasetNew = [
    ...dataset.map((d) =>
      Object.assign(
        {},
        {
          data: d.data.map((d1) => Object.assign({}, d1)),
          name: d.name,
          unit: d.unit,
        }
      )
    ),
  ];

  const timeListGaps = getTimeListGaps(datasetNew);

  const labels = [{ type: 'date', label: 'time' }];
  const recsByTime = {};
  /* datasetNew [{name, unit, data:[]}] */
  datasetNew.forEach((serie) => {
    /* remove the undefined values */
    const serieData = [...serie.data.map((d) => Object.assign({}, d))];
    let dataFilter = serieData
      .map((d) => Object.assign({}, d))
      .filter((d) => typeof d.val !== 'undefined');
    if (timestampMin && timestampMax) {
      dataFilter = dataFilter.filter(
        (d) => d.timestamp >= timestampMin && d.timestamp <= timestampMax
      );
    }
    Object.assign(serie, { data: dataFilter });
    const dataIndexedByTimestamp = {};
    serie.data.forEach((d) => {
      dataIndexedByTimestamp[d.timestamp] = d;
    });
    Object.assign(serie, {
      dataIndexedByTimestamp,
      dataReducedIndexedByTimestamp: dataIndexedByTimestamp,
    });

    if (serie.data.length > width * 2) {
      const dataReducedIndexedByTimestamp = reduceDataToAmountPixels(
        serie.data,
        width
      );
      Object.assign(serie, { dataReducedIndexedByTimestamp });
    }
  });
  /* fill the object recsByTime: {timestamp: {valLine1, valLine2...}} and the array axeIndex */
  datasetNew.forEach((data, setIndex) => {
    labels.push({ label: data.name, type: 'number' });
    Object.keys(data.dataReducedIndexedByTimestamp).forEach((keyTimestamp) => {
      let slot = recsByTime[keyTimestamp];
      if (!slot) {
        slot = {};
      }
      if (data.dataReducedIndexedByTimestamp[keyTimestamp].val === undefined) {
        slot[setIndex] = null;
      } else {
        const val = data.dataReducedIndexedByTimestamp[keyTimestamp].val;
        // Use {v,f} object to put correct text in tooltip
        slot[setIndex] = {
          v: val,
          f: `${val} ${getAxisName(data.unit)}`,
        };
      }
      recsByTime[keyTimestamp] = slot;
    });
  });
  /* fill all the missing values in the object recsByTime */
  Object.keys(recsByTime).forEach((key) => {
    if (Object.values(recsByTime[key]).length < datasetNew.length) {
      let index = 0;
      while (index < datasetNew.length) {
        if (!recsByTime[key][index] && recsByTime[key][index] !== 0) {
          if (datasetNew[index].dataIndexedByTimestamp[key]) {
            recsByTime[key][index] =
              datasetNew[index].dataIndexedByTimestamp[key].val;
          } else {
            recsByTime[key][index] = null;
          }
        }
        index += 1;
      }
    }
    /* The rows with missing values should be removed, because this values will be
    interpreted by 0 */
    if (Object.values(recsByTime[key]).length < datasetNew.length) {
      delete recsByTime[key];
    }
  });

  const dataChart = [labels];
  Object.keys(recsByTime).forEach((ts) => {
    const tsSample = [moment.unix(ts).toDate()];
    const records = recsByTime[ts];
    for (let i = 0; i < datasetNew.length; i += 1) {
      if (!records[i] === undefined) {
        tsSample.push(null);
      } else {
        tsSample.push(records[i]);
      }
    }
    dataChart.push(tsSample);
  });

  /* Add a null entry between each gap */
  if (timeListGaps.length > 0) {
    const entry = dataChart[0].map(() => null);
    timeListGaps.forEach((t) => {
      const key = dataChart
        .slice(1, dataChart.length - 1)
        .findIndex((d) => moment(d[0]).unix() > t);
      if (key > 0) {
        dataChart.splice(key + 1, 0, entry);
      }
    });
  }

  // http://jira.st.local/jira/browse/WEB-567 show error if only have labels without any data
  datasetNew = datasetNew.filter((data) => data.data.length);

  const { series, vAxes } = getVAxisOptionsFromData({
    dataSet: datasetNew,
    axisStyle,
    withLabels,
    height,
    maxVAxis,
  });
  return {
    dataChart,
    series,
    vAxes,
  };
};

function getVAxisOptionsFromData({
  dataSet,
  axisStyle,
  withLabels,
  height,
  maxVAxis,
}) {
  const axeList = getAxisFromData({ dataSet, height, maxVAxis });
  const vAxes =
    axeList.length === 0
      ? [
          {
            textPosition: 'none',
            ...axisStyle,
          },
        ]
      : {};
  axeList.forEach((item, i) => {
    vAxes[i] = {
      title: item.name,
      ticks: item.ticks,
      viewWindowMode: 'explicit',
      viewWindow: {
        max: min(item.ticks),
        min: min(item.ticks),
      },
      ...axisStyle,
    };
    if (withLabels) {
      vAxes[i] = {
        ticks: item.ticks,
        label: item.name,
        viewWindowMode: 'explicit',
        viewWindow: {
          max: max(item.ticks),
          min: min(item.ticks),
        },
        ...axisStyle,
        textPosition: 'none',
        ...axisStyle,
      };
    }
  });
  const series = {};
  dataSet.forEach((data, setIndex) => {
    if (axeList.findIndex((item) => data.unit === item.unit) !== -1)
      series[setIndex] = {
        targetAxisIndex: axeList.findIndex((item) => data.unit === item.unit),
      };
  });

  return { vAxes, series };
}

function getAxisFromData({ dataSet, height, maxVAxis }) {
  const axeDic = {};
  dataSet.forEach((dataSet) => {
    if (!axeDic[dataSet.unit]) {
      axeDic[dataSet.unit] = {
        unit: dataSet.unit,
        occurrences: 1,
        name: getAxisName(dataSet.unit),
        minVale: min(dataSet.data.map((item) => item.val)),
        maxVale: max(dataSet.data.map((item) => item.val)),
      };
    } else {
      const axe = axeDic[dataSet.unit];
      axe.occurrences += 1;
      axe.minVale = min(
        dataSet.data.map((item) => item.val).concat(axe.minVale)
      );
      axe.maxVale = max(
        dataSet.data.map((item) => item.val).concat(axe.maxVale)
      );
    }
  });
  const axeList = Object.values(axeDic)
    .map((item) => ({
      ...item,
      ticks: getTicks(item.minVale, item.maxVale, height),
    }))
    .sort((a, b) => b.occurrences - a.occurrences)
    .slice(0, maxVAxis);

  return axeList;
}

function getTicks(min, max, height = 400) {
  const sections = Math.round(height / 50);
  // 1: avoid having max == min
  let diff = max - min || 1;
  const precision = Math.floor(Math.log10(diff));
  min = roundExp(min, precision, Math.floor);
  max = roundExp(max, precision, Math.ceil);
  diff = max - min;
  let delta = diff / (sections - 1);
  const listSections = new Array(sections)
    .fill(0)
    .map((_, i) => min + i * delta);
  /** get closer number with cool round */
  const listApproximated = uniq(
    listSections.map((num) => roundExp(num, precision))
  );
  return listApproximated;
}
/**
 * Get coords
 * @param object chart
 * @return object zoom
 */
export const getCoords = (chart) => {
  if (!chart) {
    return null;
  }
  const chartLayout = chart.getChartLayoutInterface();
  const chartBounds = chartLayout.getChartAreaBoundingBox();
  const zoom = {
    min: chartLayout.getHAxisValue(chartBounds.left),
    max: chartLayout.getHAxisValue(chartBounds.width + chartBounds.left),
  };
  return zoom;
};
